import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImagePlus, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { FileUploadService } from '@/services/FileUploadService';
import { toast } from 'sonner';

interface BusinessImage {
  id: string;
  url: string;
  caption?: string;
}

interface BusinessImageGalleryProps {
  businessId: string;
  images: BusinessImage[];
  onImagesChange: (images: BusinessImage[]) => void;
  maxImages?: number;
  disableUpload?: boolean;
}

export const BusinessImageGallery: React.FC<BusinessImageGalleryProps> = ({
  businessId,
  images,
  onImagesChange,
  maxImages = 10,
  disableUpload = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [optimizedImages, setOptimizedImages] = useState<Record<string, string>>({});

  // Effect to optimize base64 images
  useEffect(() => {
    const optimizeImages = async () => {
      const optimized: Record<string, string> = {};
      
      for (const image of images) {
        if (image.url.startsWith('data:image')) {
          try {
            // Create an optimized version if we haven't already
            if (!optimizedImages[image.id]) {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const img = new Image();
              
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = image.url;
              });
              
              // Set maximum dimensions while maintaining aspect ratio
              const maxDim = 800;
              let width = img.width;
              let height = img.height;
              
              if (width > height && width > maxDim) {
                height = (height * maxDim) / width;
                width = maxDim;
              } else if (height > maxDim) {
                width = (width * maxDim) / height;
                height = maxDim;
              }
              
              canvas.width = width;
              canvas.height = height;
              
              if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                optimized[image.id] = canvas.toDataURL('image/jpeg', 0.7);
              }
            }
          } catch (error) {
            console.error('Error optimizing image:', error);
            optimized[image.id] = image.url; // Fall back to original
          }
        } else {
          optimized[image.id] = image.url; // Keep non-base64 URLs as is
        }
      }
      
      setOptimizedImages(optimized);
    };
    
    optimizeImages();
  }, [images]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the maximum
    if (images.length + files.length > maxImages) {
      toast.error(`You can only upload up to ${maxImages} images`);
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          throw new Error(`${file.name} exceeds 5MB size limit`);
        }

        // Upload file
        const result = await FileUploadService.uploadFile(
          file,
          {
            path: `businesses/${businessId}/gallery/${Date.now()}-${file.name}`,
            maxSizeInMB: 5,
            allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
            useLocalFallback: true
          }
        );

        if (!result) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          url: result.url
        };
      });

      const newImages = await Promise.all(uploadPromises);
      onImagesChange([...images, ...newImages]);
      toast.success(`Successfully uploaded ${files.length} image${files.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error uploading images:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload images');
      toast.error('Failed to upload one or more images');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (imageId: string) => {
    onImagesChange(images.filter(img => img.id !== imageId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Business Gallery</Label>
        <span className="text-sm text-gray-500">
          {images.length} / {maxImages} images
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Existing Images */}
        {images.map((image) => (
          <div
            key={image.id}
            className="relative aspect-square group border rounded-lg overflow-hidden bg-gray-50"
          >
            {optimizedImages[image.id] ? (
              <img
                src={optimizedImages[image.id]}
                alt="Gallery"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = image.url; // Fall back to original URL if optimized version fails
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => handleRemoveImage(image.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        {/* Upload Button */}
        {images.length < maxImages && !disableUpload && (
          <div className="aspect-square border-2 border-dashed rounded-lg flex items-center justify-center">
            <label
              htmlFor="gallery-upload"
              className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-500">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <ImagePlus className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-500">Add Images</span>
                </div>
              )}
              <input
                id="gallery-upload"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
            </label>
          </div>
        )}
      </div>

      {uploadError && (
        <p className="text-sm text-red-500 mt-2">{uploadError}</p>
      )}

      <p className="text-sm text-gray-500">
        Upload up to {maxImages} images (max 5MB each). Supported formats: JPEG, PNG, WebP
      </p>
    </div>
  );
}; 